// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { ArrowLeftRight, Check } from "lucide-react"
import type { MealInstance, OnboardingStateResponse } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { computeFitScore, type FitScore } from "@/lib/fitScore"
import { trackFunnelEvent } from "@/lib/analytics"
import { Button } from "@/components/ui/button"
import { useI18n, type Locale } from "@/i18n"
import { formatEurFromCents } from "@/i18n/format"
import { mealMinutesLabel } from "@/lib/mealMinutes"

/**
 * Post-onboarding value reveal, conversion pass 2026-08-30 (Fable 5 +
 * conversion-copywriting): the user's OWN answers mirrored back as kept
 * promises ("You said → we kept it"), one real dish from their generated
 * week teased (open loop into the plan), and the fit score demoted to a
 * compact seal. Every value is computed from the actual plan; missing
 * limits render as honest "you skipped X" rows. Mirrors mobile
 * plan-ready.tsx. Funnel-safe: any failure skips silently onward — to
 * /app/today when free-access activation already succeeded (ADR-083,
 * `location.state.alreadyActivated`), /app/access otherwise.
 */

interface Step9Answer {
  timePerMealMinutes?: number
}
interface Step5Answer {
  dietaryStyle?: "omnivore" | "pescatarian" | "vegetarian" | "vegan"
}

const GOAL_LABEL_KEY: Record<string, string> = {
  maintain: "onboarding.step3.maintain",
  loss: "onboarding.step3.loss",
  gain: "onboarding.step3.gain",
  improve_health: "onboarding.step3.improve",
}

interface Receipt {
  said: string
  kept: string
  ok: boolean
}

function humanizeRecipeId(recipeId: string): string {
  let words = recipeId.replace(/^rec_/, "").split("_").filter(Boolean)
  // Consumer UX gate (2026-09-02): internal diet-class prefixes and content
  // hashes are implementation detail, never part of a dish's name.
  if (words.length > 1 && ["omni", "pesc", "veg"].includes(words[0])) words = words.slice(1)
  while (words.length > 1 && /^[0-9a-f]{6,}$/.test(words[words.length - 1])) words.pop()
  if (words.length === 0) return recipeId
  return words.map((w, i) => (i === 0 ? w[0].toUpperCase() + w.slice(1) : w)).join(" ")
}

function intlLocale(locale: Locale): string {
  return locale === "de" ? "de-DE" : "en-DE"
}

export function PlanReadyPage() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const reduce = useReducedMotion() ?? false
  const state = location.state as { pendingPlanJobId?: string; alreadyActivated?: boolean } | null
  const pendingPlanJobId = state?.pendingPlanJobId
  // ADR-083: AccessPage sends this once the automatic free-access
  // activation is confirmed — there is no unclaimed offer left to show, so
  // the CTA (and any failure here) goes straight to Today instead of
  // bouncing back through a now-pointless paywall stop.
  const alreadyActivated = state?.alreadyActivated ?? false
  const [view, setView] = useState<{
    receipts: Receipt[]
    score: FitScore
    tease: MealInstance
    teaseDayLabel: string
    firstName: string | null
    days: number
    meals: number
  } | null>(null)

  const goToNext = () =>
    alreadyActivated
      ? navigate("/app/today", { replace: true })
      : navigate("/app/access", {
          replace: true,
          state: pendingPlanJobId ? { pendingPlanJobId } : undefined,
        })

  useEffect(() => {
    let cancelled = false
    Promise.all([api.getCurrentPlan(), api.getProfile(), api.getOnboarding()])
      .then(([plan, profile, onboarding]) => {
        if (cancelled) return
        const answers = (onboarding as OnboardingStateResponse).answers
        const step9 = answers.find(a => a.step === 9)?.answer as Step9Answer | undefined
        const step5 = answers.find(a => a.step === 5)?.answer as Step5Answer | undefined
        const score = computeFitScore({
          calorieTargetKcal: profile.calorieTarget?.kcal ?? null,
          validationStatus: plan.lastValidation.status,
          meals: plan.meals.map(m => ({
            recipeId: m.recipeId,
            slot: m.slot,
            activeTimeMinutes: m.activeTimeMinutes,
            // Cost truth: an unavailable estimate is NOT a €0 price — skipped, never summed as 0.
            estimatedCostCents:
              m.estimatedCostPerServing.status === "unavailable"
                ? null
                : m.estimatedCostPerServing.amountCents,
            servings: m.servings,
          })),
          timePerMealMinutes: step9?.timePerMealMinutes ?? null,
          weeklyBudgetEurCents: profile.budget.hasTarget ? profile.budget.weeklyEurCents : null,
        })
        if (score === null) {
          goToNext()
          return
        }

        const receipts: Receipt[] = []
        const goalKey = GOAL_LABEL_KEY[profile.goalType]
        if (profile.calorieTarget && goalKey) {
          receipts.push({
            said: t("prehook.saidGoal", { goal: t(goalKey) }),
            kept: t("prehook.receiptGoal", { kcal: `${profile.calorieTarget.kcal} kcal` }),
            ok: true,
          })
        }
        const timeSub = score.subs.find(s => s.key === "time")!
        if (timeSub.receipt.kind === "timeNoLimit") {
          receipts.push({
            said: t("prehook.saidNoTime"),
            kept: t("prehook.receiptNoTime"),
            ok: true,
          })
        } else if (timeSub.receipt.kind === "timeOk") {
          receipts.push({
            said: t("prehook.saidTime", { limit: timeSub.receipt.limit }),
            kept: t("prehook.receiptTimeOk", { avg: timeSub.receipt.avg }),
            ok: true,
          })
        } else if (timeSub.receipt.kind === "timeOver") {
          receipts.push({
            said: t("prehook.saidTime", { limit: timeSub.receipt.limit }),
            kept: t("prehook.receiptTimeOver", { avg: timeSub.receipt.avg }),
            ok: false,
          })
        }
        const budgetSub = score.subs.find(s => s.key === "budget")!
        if (budgetSub.receipt.kind === "budgetNoLimit") {
          receipts.push({
            said: t("prehook.saidNoBudget"),
            kept: t("prehook.receiptNoBudget"),
            ok: true,
          })
        } else if (budgetSub.receipt.kind === "budgetOk") {
          receipts.push({
            said: t("prehook.saidBudget", {
              budget: formatEurFromCents(profile.budget.weeklyEurCents ?? 0, locale),
            }),
            kept: t("prehook.receiptBudgetOk", {
              cost: formatEurFromCents(score.weekCostCents, locale),
              delta: formatEurFromCents(budgetSub.receipt.underCents, locale),
            }),
            ok: true,
          })
        } else if (budgetSub.receipt.kind === "budgetOver") {
          receipts.push({
            said: t("prehook.saidBudget", {
              budget: formatEurFromCents(profile.budget.weeklyEurCents ?? 0, locale),
            }),
            kept: t("prehook.receiptBudgetOver", {
              cost: formatEurFromCents(score.weekCostCents, locale),
              delta: formatEurFromCents(budgetSub.receipt.overCents, locale),
            }),
            ok: false,
          })
        }
        const tasteSub = score.subs.find(s => s.key === "taste")!
        const styleLabel = step5?.dietaryStyle ? t(`onboarding.step5.${step5.dietaryStyle}`) : null
        if (tasteSub.receipt.kind === "tastePass") {
          receipts.push({
            said: styleLabel ? t("prehook.saidFood", { style: styleLabel }) : "",
            kept: t("prehook.receiptFood", {
              meals: plan.meals.length,
              distinct: tasteSub.receipt.distinctDishes,
            }),
            ok: true,
          })
        } else {
          receipts.push({
            said: styleLabel ? t("prehook.saidFood", { style: styleLabel }) : "",
            kept: t("prehook.receiptFoodPending"),
            ok: false,
          })
        }

        const sorted = [...plan.meals].sort((a, b) => a.date.localeCompare(b.date))
        const tease = sorted.find(m => m.slot === "dinner") ?? sorted[0]!
        const teaseDayLabel = t("prehook.teaseSlot", {
          day: new Intl.DateTimeFormat(intlLocale(locale), {
            weekday: "long",
            timeZone: "UTC",
          }).format(new Date(`${tease.date}T00:00:00Z`)),
          slot: t(`views.slots.${tease.slot}`),
        })

        setView({
          receipts,
          score,
          tease,
          teaseDayLabel,
          firstName: profile.personalInfo.firstName,
          days: new Set(plan.meals.map(m => m.date)).size,
          meals: plan.meals.length,
        })
        // G04: only once the teaser has real data to show — a load failure
        // skips straight to goToNext() above and never fires this.
        trackFunnelEvent("plan_ready_viewed")
      })
      .catch(() => {
        if (!cancelled) goToNext()
      })
    return () => {
      cancelled = true
    }
    // goToNext is stable-by-construction (navigate + location state only)
    // Deliberately narrow dependency list.
  }, [api, t, locale])

  if (!view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p className="text-sm text-ink-2">{t("common.loading")}</p>
      </div>
    )
  }

  const teaseTitle = view.tease.title?.[locale] ?? humanizeRecipeId(view.tease.recipeId)
  const RING_R = 26
  const CIRC = 2 * Math.PI * RING_R
  const rise = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { delay },
  })

  return (
    <div className="flex min-h-screen flex-col items-center bg-cream px-6 py-12">
      <div className="flex w-full max-w-md flex-1 flex-col gap-5">
        <motion.div {...rise(0)}>
          <p className="text-xs font-semibold tracking-[0.14em] text-green">
            {view.firstName
              ? t("prehook.eyebrowNamed", { name: view.firstName.toUpperCase() })
              : t("prehook.eyebrow")}
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-ink text-balance">
            {t("prehook.title")}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            {t("prehook.intro", { days: view.days, meals: view.meals })}
          </p>
        </motion.div>

        {/* Kept-promise receipts — their words, stamped. */}
        <div className="flex flex-col gap-2.5">
          {view.receipts.map((receipt, index) => (
            <motion.div
              key={index}
              className="flex items-start gap-3 rounded-bb-md border border-line bg-paper p-4"
              {...rise(0.12 + index * 0.11)}
            >
              <span
                className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full ${
                  receipt.ok ? "bg-green text-paper" : "bg-green/10 text-green"
                }`}
              >
                {receipt.ok ? (
                  <Check className="size-3.5" aria-hidden="true" />
                ) : (
                  <ArrowLeftRight className="size-3.5" aria-hidden="true" />
                )}
              </span>
              <span className="flex flex-col gap-0.5">
                {receipt.said ? (
                  <span className="text-xs font-semibold text-muted">{receipt.said}</span>
                ) : null}
                <span className="text-sm leading-snug font-semibold text-ink">{receipt.kept}</span>
              </span>
            </motion.div>
          ))}
        </div>

        {/* The open loop: one real dish, the rest withheld. */}
        <motion.div
          className="flex flex-col gap-2.5 rounded-bb-xl bg-green p-5 text-paper shadow-lg"
          initial={reduce ? false : { opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.62, type: "spring", damping: 20 }}
        >
          <p className="text-[11px] font-semibold tracking-wide uppercase text-paper/70">
            {t("prehook.teaseHeading")}
          </p>
          <div className="flex items-center gap-4">
            {/* Debt 37: the first dish a new user sees is named, never drawn
                by a hash that could contradict the diet they just chose. */}
            <div className="flex min-w-0 flex-col">
              <span className="text-xs font-semibold text-paper/70">{view.teaseDayLabel}</span>
              <span className="truncate font-display text-lg font-semibold">{teaseTitle}</span>
              <span className="text-sm tabular-nums text-paper/80">
                {mealMinutesLabel(view.tease.activeTimeMinutes, view.tease.totalTimeMinutes, t)} · {Math.round(view.tease.nutrition.kcal)} kcal
              </span>
            </div>
          </div>
          <p className="text-xs italic text-paper/70">
            {t("prehook.teaseMore", { count: view.meals - 1 })}
          </p>
        </motion.div>

        {/* Fit seal — evidence, not the hero. */}
        <motion.div className="flex items-center gap-3" {...rise(0.75)}>
          <div className="relative size-16">
            <svg viewBox="0 0 64 64" className="size-full -rotate-90">
              <circle
                cx="32"
                cy="32"
                r={RING_R}
                fill="none"
                strokeWidth="6"
                className="stroke-paper"
              />
              <motion.circle
                cx="32"
                cy="32"
                r={RING_R}
                fill="none"
                strokeWidth="6"
                strokeLinecap="round"
                className="stroke-green"
                strokeDasharray={CIRC}
                initial={{ strokeDashoffset: reduce ? CIRC * (1 - view.score.total / 100) : CIRC }}
                animate={{ strokeDashoffset: CIRC * (1 - view.score.total / 100) }}
                transition={{ duration: reduce ? 0 : 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums text-ink">
              {view.score.total}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-ink-2">{t("prehook.fitSeal")}</span>
            <span className="text-base font-bold text-ink">
              {view.score.total}
              {t("prehook.scoreOf")}
            </span>
          </div>
        </motion.div>

        <motion.div className="mt-auto flex flex-col gap-2 pb-4" {...rise(0.85)}>
          <Button type="button" className="min-h-12 w-full text-base" onClick={goToNext}>
            {t("prehook.cta")}
          </Button>
          <p className="text-center text-xs font-semibold text-green">{t("prehook.ctaNote")}</p>
          <p className="text-center text-xs text-muted">{t("prehook.footnote")}</p>
        </motion.div>
      </div>
    </div>
  )
}
